import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { setColourBlindColours } from "../lib/colourBlind";

export type Mode = "dark" | "light" | "colorblind";

/**
 * Which logo colour leads. Both are taken from the Abacas logo (public/abacas-logo.png):
 *
 *   plumage  the main app. Every logo colour has one job: shield magenta for actions, ember
 *            orange for "you are here" markers (active tab, sidebar marker, ruler needle, corner
 *            ticks — never text on a light ground, it is too pale), royal-wing blue for references
 *            and links, violet-tail ink for the drawing lines and the engineering-paper grid.
 *   wing     the Jersey edition. The same system with the blue wing leading actions, so it reads
 *            as a sibling of the main app rather than a copy.
 *
 * The colour-blind theme keeps the Wong (2011) status colours (blue / orange / vermilion), so any
 * accent or marker that would be mistaken for a status there moves to magenta or violet. Status is
 * also carried by shape (components/Drawing.tsx StatusTag), never by hue alone.
 */
export type Palette = "plumage" | "wing";

// Roles shared by every palette — status bands and the consultant / auditor tones.
const STATUS_DARK = {
  success: "#22C55E", successText: "#86EFAC", successBg: "rgba(34,197,94,0.10)",
  warning: "#F59E0B", warningText: "#FCD34D", warningBg: "rgba(245,158,11,0.10)",
  danger:  "#EF4444", dangerText:  "#FCA5A5", dangerBg:  "rgba(239,68,68,0.10)",
  consultant: "#C084FC", consultantBg: "#2D1155",
  auditor: "#22C55E", auditorBg: "#052E16",
  riskLowBg: "rgba(34,197,94,0.12)",  riskLowText: "#86EFAC",
  riskMedBg: "rgba(245,158,11,0.12)", riskMedText: "#FCD34D",
  riskHighBg: "rgba(249,115,22,0.12)",riskHighText: "#FDBA74",
  riskCritBg: "rgba(239,68,68,0.12)", riskCritText: "#FCA5A5",
};

const STATUS_LIGHT = {
  success: "#16a34a", successText: "#15803d", successBg: "#dcfce7",
  warning: "#b45309", warningText: "#92400e", warningBg: "#fef3c7",
  danger:  "#dc2626", dangerText:  "#991b1b", dangerBg:  "#fee2e2",
  consultant: "#7c3aed", consultantBg: "#ede9fe",
  auditor: "#16a34a", auditorBg: "#dcfce7",
  riskLowBg: "#dcfce7",  riskLowText: "#15803d",
  riskMedBg: "#fef9c3",  riskMedText: "#92400e",
  riskHighBg: "#ffedd5", riskHighText: "#9a3412",
  riskCritBg: "#fee2e2", riskCritText: "#991b1b",
};

// Wong (2011) colorblind-safe status colors — distinct for deuteranopia, protanopia & tritanopia
const STATUS_COLORBLIND = {
  ...STATUS_LIGHT,
  success: "#0072B2", successText: "#004d80", successBg: "#ddeeff",
  warning: "#E69F00", warningText: "#7a5000", warningBg: "#fff3cc",
  danger:  "#D55E00", dangerText:  "#7a3500", dangerBg:  "#ffe8d6",
  riskLowBg: "#ddeeff",  riskLowText: "#004d80",
  riskMedBg: "#fff3cc",  riskMedText: "#7a5000",
  riskHighBg: "#ffe8d6", riskHighText: "#7a3500",
  riskCritBg: "#fde8d8", riskCritText: "#6b2900",
  // The auditor tone is green elsewhere; here green would read as "met", so it goes blue.
  auditor: "#005f8f", auditorBg: "#ddeeff",
};

// ── Plumage (main app) ────────────────────────────────────────────────────

const PLUMAGE_DARK = {
  bg: "#0a0624", panel: "#130d30", panelAlt: "#1b1440", inputBg: "#1f1745",
  border: "#2a2156", borderStrong: "#3d3275", text: "#f4f0fe", heading: "#FFFFFF",
  muted: "#cfc7ec", subtle: "#b7aedb", faint: "#9289c0",
  accent: "#c21a9b", accentStrong: "#9c1580", accentText: "#f07fd6", accentBg: "#2a0a2c",
  mark: "#f37121", ref: "#5aa9f0", line: "#4a3d80",
  gridMinor: "rgba(120,80,220,0.07)", gridMajor: "rgba(120,80,220,0.16)",
  sheetShadow: "6px 6px 0 rgba(0,0,0,0.45)",
  ...STATUS_DARK,
};

const PLUMAGE_LIGHT = {
  bg: "#fbf8ff", panel: "#ffffff", panelAlt: "#f3eefb", inputBg: "#ffffff",
  border: "#e4d8f7", borderStrong: "#c9b0ef", text: "#1a0f33", heading: "#0f0520",
  muted: "#6b4d9a", subtle: "#8b6fb5", faint: "#b49fd8",
  accent: "#b31385", accentStrong: "#8f0f6a", accentText: "#9a1072", accentBg: "#fbe6f4",
  mark: "#f37121", ref: "#1e62d2", line: "#2a1660",
  gridMinor: "rgba(83,36,163,0.05)", gridMajor: "rgba(83,36,163,0.13)",
  sheetShadow: "6px 6px 0 rgba(42,22,96,0.16)",
  ...STATUS_LIGHT,
};

const PLUMAGE_COLORBLIND: typeof PLUMAGE_LIGHT = {
  ...PLUMAGE_LIGHT,
  // Orange and blue are status colours here, so the marker and references go violet.
  mark: "#5324a3", ref: "#5324a3",
  ...STATUS_COLORBLIND,
};

// ── Wing (Jersey edition) ─────────────────────────────────────────────────

const WING_DARK: typeof PLUMAGE_DARK = {
  bg: "#060d22", panel: "#0c1630", panelAlt: "#122042", inputBg: "#16264d",
  border: "#1c2c55", borderStrong: "#2b4278", text: "#f0f5fd", heading: "#FFFFFF",
  muted: "#c8d4ee", subtle: "#adbcde", faint: "#8293bb",
  accent: "#2a6fd6", accentStrong: "#1f58b0", accentText: "#7db8f5", accentBg: "#0d2147",
  mark: "#f37121", ref: "#e879c9", line: "#2b4a80",
  gridMinor: "rgba(13,129,214,0.07)", gridMajor: "rgba(13,129,214,0.16)",
  sheetShadow: "6px 6px 0 rgba(0,0,0,0.45)",
  ...STATUS_DARK,
};

const WING_LIGHT: typeof PLUMAGE_LIGHT = {
  bg: "#f6f9fe", panel: "#ffffff", panelAlt: "#edf3fb", inputBg: "#ffffff",
  border: "#d6e3f5", borderStrong: "#a9c3e8", text: "#0f1e3a", heading: "#081429",
  muted: "#4a5f85", subtle: "#6a7ea3", faint: "#a3b4d0",
  accent: "#1e62d2", accentStrong: "#174ea8", accentText: "#174ea8", accentBg: "#e3eefc",
  mark: "#f37121", ref: "#b31385", line: "#0f2a55",
  gridMinor: "rgba(13,129,214,0.05)", gridMajor: "rgba(13,129,214,0.13)",
  sheetShadow: "6px 6px 0 rgba(15,42,85,0.16)",
  ...STATUS_LIGHT,
};

const WING_COLORBLIND: typeof PLUMAGE_LIGHT = {
  ...WING_LIGHT,
  // Blue is the "met" status colour here, so actions go violet and markers magenta.
  accent: "#5324a3", accentStrong: "#401b80", accentText: "#401b80", accentBg: "#ece6f8",
  mark: "#b31385", ref: "#b31385",
  ...STATUS_COLORBLIND,
};

const PALETTES: Record<Palette, Record<Mode, typeof PLUMAGE_LIGHT>> = {
  plumage: { dark: PLUMAGE_DARK, light: PLUMAGE_LIGHT, colorblind: PLUMAGE_COLORBLIND },
  wing:    { dark: WING_DARK,    light: WING_LIGHT,    colorblind: WING_COLORBLIND },
};

function getPalette(palette: Palette, mode: Mode) {
  return PALETTES[palette][mode] ?? PALETTES[palette].dark;
}

/** The resolved colour values of a palette in a mode (for a canvas, which cannot read var()). */
export function paletteValues(palette: Palette, mode: Mode): typeof PLUMAGE_LIGHT {
  return getPalette(palette, mode);
}

// ── Nebula (components/BlueprintBackdrop.tsx) ─────────────────────────────
//
// The clouds that drift behind the engineering paper, from the same logo colours: in Plumage the
// shield magenta and the violet tail lead, the royal wing and an ember fleck sit at the edges; in
// Wing the blue leads. The colour-blind theme keeps to magenta and violets, because blue and orange
// are status colours there. `gain` scales every cloud: the light themes are a pale wash.

export type Rgb = [number, number, number];

export interface NebulaSpec {
  /** One colour per cloud, in the order of NEBULA_LAYOUT. */
  clouds: Rgb[];
  /** A grid walker's colour where no cloud reaches it. */
  ink: Rgb;
  gain: number;
}

/** Where each cloud sits (fraction of the surface), its reach and its phase in the drift. */
export const NEBULA_LAYOUT = [
  { px: 0.22, py: 0.30, r: 0.38, phase: 0 },
  { px: 0.60, py: 0.18, r: 0.42, phase: 2 },
  { px: 0.80, py: 0.72, r: 0.34, phase: 4 },
  { px: 0.92, py: 0.10, r: 0.18, phase: 1 },
  { px: 0.35, py: 0.85, r: 0.30, phase: 3 },
] as const;

const MAGENTA: Rgb = [194, 26, 155], VIOLET: Rgb = [110, 60, 210], DEEP_VIOLET: Rgb = [83, 36, 163];
const ROYAL: Rgb = [90, 169, 240], EMBER: Rgb = [243, 113, 33];
const WING_BLUE: Rgb = [42, 111, 214], SKY: Rgb = [13, 129, 214], ORCHID: Rgb = [232, 121, 201], NAVY: Rgb = [43, 74, 128];

const NEBULAE: Record<Palette, Record<Mode, NebulaSpec>> = {
  plumage: {
    dark:       { clouds: [MAGENTA, VIOLET, ROYAL, EMBER, DEEP_VIOLET], ink: [150, 110, 240], gain: 1 },
    light:      { clouds: [MAGENTA, VIOLET, ROYAL, EMBER, DEEP_VIOLET], ink: [83, 36, 163], gain: 0.32 },
    colorblind: { clouds: [MAGENTA, VIOLET, DEEP_VIOLET, MAGENTA, VIOLET], ink: [83, 36, 163], gain: 0.28 },
  },
  wing: {
    dark:       { clouds: [WING_BLUE, NAVY, ORCHID, EMBER, SKY], ink: [80, 150, 235], gain: 1 },
    light:      { clouds: [WING_BLUE, SKY, ORCHID, EMBER, NAVY], ink: [15, 42, 85], gain: 0.3 },
    colorblind: { clouds: [DEEP_VIOLET, MAGENTA, VIOLET, MAGENTA, DEEP_VIOLET], ink: [83, 36, 163], gain: 0.28 },
  },
};

export function nebulaFor(palette: Palette, mode: Mode): NebulaSpec {
  return NEBULAE[palette][mode] ?? NEBULAE[palette].dark;
}

function applyPalette(p: typeof PLUMAGE_LIGHT) {
  const root = document.documentElement;
  for (const [key, val] of Object.entries(p)) {
    root.style.setProperty(`--c-${key}`, val);
  }
}

interface ThemeCtx {
  mode: Mode;
  palette: Palette;
  cycle: () => void;
  /** Whether the background drifts (BlueprintBackdrop); off draws one still frame. Per browser. */
  motion: boolean;
  toggleMotion: () => void;
}
const Ctx = createContext<ThemeCtx>({ mode: "dark", palette: "plumage", cycle: () => {}, motion: true, toggleMotion: () => {} });

function readMotion(): boolean {
  try { return localStorage.getItem("backgroundMotion") !== "off"; } catch { return true; }
}

export function ThemeProvider({ children, palette = "plumage" }: { children: ReactNode; palette?: Palette }) {
  const [mode, setMode] = useState<Mode>(() => (localStorage.getItem("theme") as Mode) ?? "dark");
  const [motion, setMotion] = useState<boolean>(readMotion);

  function toggleMotion() {
    setMotion((m) => {
      try { localStorage.setItem("backgroundMotion", m ? "off" : "on"); } catch { /* private window */ }
      return !m;
    });
  }

  useEffect(() => {
    const p = getPalette(palette, mode);
    applyPalette(p);
    document.body.style.background = p.bg;
    localStorage.setItem("theme", mode);
  }, [mode, palette]);

  useState(() => { applyPalette(getPalette(palette, mode)); });

  // Hex colours written in pages follow the colour-blind theme through cbSafe(); set before the
  // children render so they read the new mode.
  setColourBlindColours(mode === "colorblind");

  function cycle() {
    setMode((m) => m === "dark" ? "light" : m === "light" ? "colorblind" : "dark");
  }

  return <Ctx.Provider value={{ mode, palette, cycle, motion, toggleMotion }}>{children}</Ctx.Provider>;
}

export function useThemeMode() { return useContext(Ctx); }
